import api from "../api";

const CACHE_VERSION = 1;
const CACHE_PREFIX = `enterprise_logo_cache_v${CACHE_VERSION}:`;
const DEFAULT_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

const IGNORED_SUBDOMAINS = new Set(["www", "app", "api", "dev", "staging", "test", "qa"]);

const normalizeTenantKey = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed ? trimmed : null;
};

const isValidTenantKey = (value) => {
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value);
};

export const detectEnterpriseFromLocation = ({
  hostname = window.location.hostname,
  search = window.location.search,
  baseDomain = process.env.REACT_APP_TENANT_BASE_DOMAIN,
} = {}) => {
  const params = new URLSearchParams(search || "");

  const enterpriseIdRaw = params.get("enterprise_id") || params.get("enterpriseId");
  const enterpriseId = Number.parseInt(String(enterpriseIdRaw || ""), 10);
  if (Number.isFinite(enterpriseId) && enterpriseId > 0) {
    return { enterpriseId, tenantKey: null, source: "query_enterprise_id" };
  }

  const queryTenant =
    params.get("tenant") ||
    params.get("enterprise") ||
    params.get("company") ||
    params.get("subdomain");
  const normalizedQueryTenant = normalizeTenantKey(queryTenant);
  if (normalizedQueryTenant && isValidTenantKey(normalizedQueryTenant)) {
    return { enterpriseId: null, tenantKey: normalizedQueryTenant, source: "query" };
  }

  const host = (hostname || "").split(":")[0].toLowerCase();
  if (!host || host === "localhost") return { enterpriseId: null, tenantKey: null, source: null };

  const normalizedBase = normalizeTenantKey(baseDomain);
  if (normalizedBase && host.endsWith(normalizedBase)) {
    const prefix = host.slice(0, host.length - normalizedBase.length).replace(/\.$/, "");
    if (prefix) {
      const labels = prefix.split(".").filter(Boolean);
      const candidate = labels[labels.length - 1];
      if (candidate && isValidTenantKey(candidate)) {
        return { enterpriseId: null, tenantKey: candidate, source: "subdomain" };
      }
    }
  }

  const parts = host.split(".").filter(Boolean);
  if (parts.length >= 3) {
    const first = parts[0];
    const candidate = IGNORED_SUBDOMAINS.has(first) && parts.length >= 4 ? parts[1] : first;
    if (candidate && isValidTenantKey(candidate)) {
      return { enterpriseId: null, tenantKey: candidate, source: "subdomain" };
    }
  }

  return { enterpriseId: null, tenantKey: null, source: null };
};

const cacheKeyFor = ({ tenantKey, enterpriseId }) => {
  if (enterpriseId) return `${CACHE_PREFIX}enterprise_id:${enterpriseId}`;
  if (tenantKey) return `${CACHE_PREFIX}tenant:${tenantKey}`;
  return null;
};

export const readCachedEnterpriseLogo = ({ tenantKey, enterpriseId }) => {
  const key = cacheKeyFor({ tenantKey, enterpriseId });
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.logoUrl) return null;
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const writeCachedEnterpriseLogo = ({ tenantKey, enterpriseId, logoUrl, etag, ttlMs }) => {
  const key = cacheKeyFor({ tenantKey, enterpriseId });
  if (!key) return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        logoUrl,
        etag: etag || null,
        expiresAt: Date.now() + (Number.isFinite(ttlMs) ? ttlMs : DEFAULT_TTL_MS),
      }),
    );
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
};

// Fetches the enterprise logo from backend public API:
// GET /public/enterprise/logo  (baseURL is REACT_APP_API_URL which should already include /api)
export const fetchEnterpriseLogo = async ({
  tenantKey,
  enterpriseId,
  etag,
  signal,
} = {}) => {
  const params = {};
  if (tenantKey) params.tenant = tenantKey;
  if (enterpriseId) params.enterprise_id = enterpriseId;

  const headers = {};
  if (etag) headers["If-None-Match"] = etag;

  const response = await api.get("/public/enterprise/logo", {
    params,
    headers,
    signal,
    skipAuth: true,
    skipLoader: true,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  });

  if (response.status === 304) {
    return {
      logoUrl: null,
      etag: response.headers?.etag || etag || null,
      cacheTtlSeconds: null,
    };
  }

  return {
    logoUrl: response.data?.logo_url || null,
    etag: response.headers?.etag || null,
    cacheTtlSeconds: response.data?.cache_ttl_seconds || null,
  };
};
